import { useEffect, useRef, useState } from 'react';
import { Check, ImageUp, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanyProfile, useUpdateCompany, useUpdateCompanyLogo } from './settings.api';

const LOGO_MAX_BYTES = 2 * 1024 * 1024;
const LOGO_MAX_PX = 480;
// Tope del data URL que acepta la API (700k); dejamos margen.
const LOGO_MAX_CHARS = 600_000;

/** Reduce el logo en el navegador. PNG conserva la transparencia; si pesa
 * demasiado se re-codifica a JPG sobre fondo blanco. */
function resizeLogo(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, LOGO_MAX_PX / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('No se pudo procesar la imagen'));
      ctx.drawImage(img, 0, 0, w, h);
      let out = canvas.toDataURL('image/png');
      if (out.length > LOGO_MAX_CHARS) {
        ctx.globalCompositeOperation = 'destination-over';
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, w, h);
        out = canvas.toDataURL('image/jpeg', 0.85);
      }
      if (out.length > LOGO_MAX_CHARS) return reject(new Error('La imagen es demasiado pesada'));
      resolve(out);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Imagen inválida'));
    };
    img.src = url;
  });
}

export function CompanyTab(): JSX.Element {
  const { data, isLoading } = useCompanyProfile();
  const update = useUpdateCompany();
  const updateLogo = useUpdateCompanyLogo();
  const fileRef = useRef<HTMLInputElement>(null);
  const [f, setF] = useState({ businessName: '', tradeName: '', address: '', ubigeoCode: '' });

  useEffect(() => {
    if (data) {
      setF({
        businessName: data.businessName ?? '',
        tradeName: data.tradeName ?? '',
        address: data.address ?? '',
        ubigeoCode: data.ubigeoCode ?? '',
      });
    }
  }, [data]);

  if (isLoading || !data) {
    return (
      <div className="cx">
        <p className="muted t-sm">Cargando…</p>
      </div>
    );
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate({
      businessName: f.businessName.trim(),
      tradeName: f.tradeName.trim(),
      address: f.address.trim(),
      ubigeoCode: f.ubigeoCode.trim(),
    });
  };

  const onPickLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') return toast.error('El logo debe ser PNG o JPG');
    if (file.size > LOGO_MAX_BYTES) return toast.error('El logo supera los 2MB');
    try {
      updateLogo.mutate(await resizeLogo(file));
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const initial = (f.businessName || data.ruc || 'M').charAt(0).toUpperCase();

  return (
    <div className="cx">
      <form className="card" style={{ maxWidth: 720 }} onSubmit={submit}>
        <div className="card-head">
          <div className="card-title">Datos de la empresa</div>
        </div>
        <div className="card-pad">
          <div className="row gap14 aic" style={{ marginBottom: 22 }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 16,
                fontSize: 26,
                fontWeight: 700,
                color: '#fff',
                display: 'grid',
                placeItems: 'center',
                background: data.logoUrl ? '#fff' : 'linear-gradient(135deg,#84CC16,#22C55E)',
                border: data.logoUrl ? '1px solid var(--border)' : undefined,
                overflow: 'hidden',
                flex: 'none',
              }}
            >
              {data.logoUrl ? (
                <img src={data.logoUrl} alt="Logo de la empresa" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : (
                initial
              )}
            </div>
            <div>
              <div className="row gap8">
                <button type="button" className="btn btn-secondary btn-sm" disabled={updateLogo.isPending} onClick={() => fileRef.current?.click()}>
                  <ImageUp size={15} />
                  {updateLogo.isPending ? 'Guardando…' : 'Cambiar logo'}
                </button>
                {data.logoUrl && (
                  <button type="button" className="btn btn-secondary btn-sm" disabled={updateLogo.isPending} onClick={() => updateLogo.mutate(null)} aria-label="Quitar logo">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg" style={{ display: 'none' }} onChange={onPickLogo} />
              <div className="t-xs muted" style={{ marginTop: 6 }}>
                PNG o JPG, máx 2MB · Se imprime en tus comprobantes
              </div>
            </div>
          </div>

          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="field">
              <label className="label">RUC</label>
              <input className="input tnum" value={data.ruc} disabled />
            </div>
            <div className="field">
              <label className="label">Moneda</label>
              <select className="select" disabled defaultValue="PEN">
                <option value="PEN">PEN — Soles (S/)</option>
                <option value="USD">USD — Dólares</option>
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label className="label">Razón social</label>
              <input className="input" value={f.businessName} required onChange={(e) => setF((s) => ({ ...s, businessName: e.target.value }))} />
            </div>
            <div className="field">
              <label className="label">Nombre comercial</label>
              <input className="input" value={f.tradeName} onChange={(e) => setF((s) => ({ ...s, tradeName: e.target.value }))} />
            </div>
            <div className="field">
              <label className="label">Ubigeo</label>
              <input className="input tnum" value={f.ubigeoCode} maxLength={6} placeholder="150101" onChange={(e) => setF((s) => ({ ...s, ubigeoCode: e.target.value }))} />
            </div>
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label className="label">Dirección fiscal</label>
              <input className="input" value={f.address} onChange={(e) => setF((s) => ({ ...s, address: e.target.value }))} />
            </div>
          </div>

          <div className="row" style={{ marginTop: 22 }}>
            <button type="submit" className="btn btn-primary mla" disabled={update.isPending}>
              <Check size={17} />
              {update.isPending ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
